/**
 * Compact financial drivers rail for Stage Finance.
 * Mutates App inputs/assumptions through the same paths as P&L.
 * Derived totals are read-only from outputs — no finance math here.
 */
import { useState } from 'react';
import { ClearableNumberInput } from './ClearableNumberInput';
import { applyUnitsChange, applyParkingSpotsChange } from '../utils/parkingSpotsLink';
import { withSyncedSalePrice } from '../utils/salePriceBlend';
import { INPUT_CONFIG, clampInputValue } from '../types/dealTypes';
import { formatMoney } from '../utils/formatters';
import { EditableOverrideRow, SourceSelect } from './pnl/EditableOverrideRow';
import {
  applyFieldOverride,
  applyCapitalSource,
  applyBankDebtAssumption,
} from '../utils/pnlInputMutations';

function SectionTitle({ children }) {
  return (
    <div className="text-[10px] md:text-[7px] font-bold text-gray-400 uppercase tracking-wider mb-1 md:mb-px">
      {children}
    </div>
  );
}

function DriverField({
  label,
  value,
  onChange,
  unit,
  step = 1,
  id,
  min,
  max,
  testId,
}) {
  const config = INPUT_CONFIG[id] || {};
  const minVal = min ?? config.min ?? 0;
  const maxVal = max ?? config.max;
  return (
    <div className="flex items-center gap-0.5">
      <label
        htmlFor={id}
        className="text-[9px] md:text-[8px] text-gray-500 uppercase w-[3.25rem] shrink-0 leading-none"
      >
        {label}
      </label>
      <div className="relative flex-1">
        <ClearableNumberInput
          id={id}
          value={value}
          onChange={onChange}
          step={step}
          min={minVal}
          max={maxVal}
          aria-label={label}
          data-testid={testId || `financial-driver-${id}`}
          className="h-[32px] md:h-6 min-h-[24px] px-2 md:px-1 text-[13px] md:text-[10px] tabular-nums border-gray-200 rounded pr-8 md:pr-6 bg-gray-50"
        />
        {unit ? (
          <span className="absolute right-1.5 md:right-1 top-1/2 -translate-y-1/2 text-[10px] md:text-[7px] text-gray-400 pointer-events-none">
            {unit}
          </span>
        ) : null}
      </div>
    </div>
  );
}

function ReadOnlyMetric({ label, value, currency, testId }) {
  return (
    <div className="flex items-center justify-between gap-1 leading-none py-0.5" data-testid={testId}>
      <span className="text-[8px] text-gray-500 uppercase">{label}</span>
      <span className="text-[10px] font-semibold tabular-nums text-gray-800">
        {formatMoney(value, currency)}
      </span>
    </div>
  );
}

function AssumptionPctField({ label, value, onChange, testId }) {
  return (
    <div className="flex items-center gap-0.5">
      <span className="text-[9px] md:text-[8px] text-gray-500 uppercase w-[3.25rem] shrink-0 leading-none">
        {label}
      </span>
      <div className="relative flex-1">
        <ClearableNumberInput
          value={value ?? 0}
          onChange={onChange}
          scale={100}
          step={0.1}
          min={0}
          max={100}
          aria-label={label}
          data-testid={testId}
          className="h-[32px] md:h-6 min-h-[24px] px-2 md:px-1 text-[13px] md:text-[10px] tabular-nums border-gray-200 rounded pr-8 md:pr-6 bg-gray-50"
        />
        <span className="absolute right-1.5 md:right-1 top-1/2 -translate-y-1/2 text-[10px] md:text-[7px] text-gray-400 pointer-events-none">
          %
        </span>
      </div>
    </div>
  );
}

/**
 * @param {object} props
 * @param {object} props.inputs
 * @param {(next: object|Function) => void} props.onInputChange
 * @param {object} props.assumptions
 * @param {(next: object|Function) => void} props.onAssumptionsChange
 * @param {object} props.outputs
 * @param {string} [props.operatingCurrency]
 * @param {Record<string,string>} [props.sourceLabels]
 */
export function FinancialDriversPanel({
  inputs = {},
  onInputChange,
  assumptions = {},
  onAssumptionsChange,
  outputs = {},
  operatingCurrency = 'CHF',
  sourceLabels = {},
}) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const cur = (operatingCurrency || 'CHF').toUpperCase();
  const perSqm = `${cur}/m2`;
  const bank = assumptions.bankDebt || {};

  const setInputField = (key) => (v) => {
    onInputChange((prev) => ({ ...prev, [key]: clampInputValue(key, v) }));
  };

  const handleOverride = (fieldKey, override) => {
    onInputChange((prev) => applyFieldOverride(prev, fieldKey, override));
  };

  const handleSourceChange = (field, source) => {
    onInputChange((prev) => applyCapitalSource(prev, field, source));
  };

  const setBankField = (key, value) => {
    onAssumptionsChange((prev) => applyBankDebtAssumption(prev, key, value));
  };

  const body = (
    <div className="flex flex-col gap-2 md:gap-1.5 text-[12px] md:text-[9px]" data-testid="financial-drivers-body">
      <section data-testid="financial-drivers-land-sales">
        <SectionTitle>Land &amp; sales</SectionTitle>
        <div className="space-y-1.5 md:space-y-px">
          <DriverField
            id="landPrice"
            label="Land"
            value={inputs.landPrice}
            onChange={setInputField('landPrice')}
            unit={cur}
            step={50000}
          />
          <DriverField
            id="landSize"
            label="Size"
            value={inputs.landSize}
            onChange={setInputField('landSize')}
            unit="m2"
            step={50}
          />
          <DriverField
            id="buildabilityCoefficient"
            label="CUS"
            value={inputs.buildabilityCoefficient}
            onChange={setInputField('buildabilityCoefficient')}
            step={0.01}
          />
          <DriverField
            id="netSaleablePercent"
            label="Net %"
            value={Math.round((Number(inputs.netSaleablePercent) || 0) * 1000) / 10}
            onChange={(v) => setInputField('netSaleablePercent')(v / 100)}
            unit="%"
            step={1}
            min={0}
            max={100}
          />
          <DriverField
            id="numberOfUnits"
            label="Units"
            value={inputs.numberOfUnits}
            onChange={(v) => onInputChange((prev) => applyUnitsChange(prev, clampInputValue('numberOfUnits', v)))}
            step={1}
          />
          <DriverField
            id="pricePerSqm"
            label={perSqm}
            value={inputs.pricePerSqm}
            onChange={setInputField('pricePerSqm')}
            unit={cur}
            step={500}
          />
          {(Number(inputs.apartmentPricePerSqm) > 0 || Number(inputs.housePricePerSqm) > 0) && (
            <div className="rounded border border-indigo-100 bg-indigo-50/40 px-1 py-0.5 space-y-px">
              {Number(inputs.apartmentPricePerSqm) > 0 && (
                <DriverField
                  id="apartmentPricePerSqm"
                  label="Apt/m2"
                  value={inputs.apartmentPricePerSqm}
                  onChange={(v) => onInputChange((prev) => withSyncedSalePrice({ apartmentPricePerSqm: v }, prev))}
                  unit={cur}
                  step={50}
                />
              )}
              {Number(inputs.housePricePerSqm) > 0 && (
                <DriverField
                  id="housePricePerSqm"
                  label="House/m2"
                  value={inputs.housePricePerSqm}
                  onChange={(v) => onInputChange((prev) => withSyncedSalePrice({ housePricePerSqm: v }, prev))}
                  unit={cur}
                  step={50}
                />
              )}
              {Number(inputs.apartmentPricePerSqm) > 0 && Number(inputs.housePricePerSqm) > 0 && (
                <DriverField
                  id="apartmentSaleableShare"
                  label="Apt %"
                  value={Math.round((Number(inputs.apartmentSaleableShare) || 0) * 100)}
                  onChange={(v) => onInputChange((prev) => withSyncedSalePrice(
                    { apartmentSaleableShare: Math.min(1, Math.max(0, v / 100)) },
                    prev,
                  ))}
                  unit="%"
                  step={5}
                  min={0}
                  max={100}
                />
              )}
            </div>
          )}
          <DriverField
            id="parkingPrice"
            label="Parking"
            value={inputs.parkingPrice}
            onChange={setInputField('parkingPrice')}
            unit={cur}
            step={5000}
          />
          <DriverField
            id="numberOfParkingSpots"
            label="Spots"
            value={inputs.numberOfParkingSpots}
            onChange={(v) => onInputChange((prev) => applyParkingSpotsChange(prev, clampInputValue('numberOfParkingSpots', v)))}
            step={1}
          />
        </div>
      </section>

      <section data-testid="financial-drivers-dev-costs">
        <SectionTitle>Development costs</SectionTitle>
        <div className="space-y-px">
          <EditableOverrideRow
            label="Notary"
            rate={outputs.notaryFeesRate}
            amount={outputs.notaryFees}
            fieldKey="notaryFees"
            onOverride={handleOverride}
            sub
            baseName="Land"
            currency={cur}
          />
          <EditableOverrideRow
            label="Other land"
            rate={outputs.otherLandFeesRate}
            amount={outputs.otherLandFees}
            fieldKey="otherLandFees"
            onOverride={handleOverride}
            sub
            baseName="Land"
            currency={cur}
          />
          <EditableOverrideRow
            label="Build"
            rate={outputs.constructionRate}
            amount={outputs.constructionCost}
            fieldKey="constructionCost"
            onOverride={handleOverride}
            baseName="Revenue"
            currency={cur}
          />
          <EditableOverrideRow
            label="Reserve"
            rate={outputs.constructionReserveRate}
            amount={outputs.constructionReserve}
            fieldKey="constructionReserve"
            onOverride={handleOverride}
            sub
            baseName="Construction"
            currency={cur}
          />
          <EditableOverrideRow
            label="Architecture"
            rate={outputs.architectureRate}
            amount={outputs.architectureCost}
            fieldKey="architectureCost"
            onOverride={handleOverride}
            sub
            baseName="Build Total"
            currency={cur}
          />
          <EditableOverrideRow
            label="Consultants"
            rate={outputs.consultantsRate}
            amount={outputs.consultantsCost}
            fieldKey="consultantsCost"
            onOverride={handleOverride}
            sub
            baseName="Build Total"
            currency={cur}
          />
          <EditableOverrideRow
            label="Contingency"
            rate={outputs.contingencyRate}
            amount={outputs.contingencyCost}
            fieldKey="contingencyCost"
            onOverride={handleOverride}
            sub
            baseName="Build Total"
            currency={cur}
          />
          <EditableOverrideRow
            label="Sales Cost"
            rate={outputs.commercializationRate}
            amount={outputs.commercializationCost}
            fieldKey="commercializationCost"
            onOverride={handleOverride}
            sub
            baseName="Revenue"
            currency={cur}
          />
          <EditableOverrideRow
            label="Pilotage"
            rate={outputs.pilotageRate}
            amount={outputs.pilotageCost}
            fieldKey="pilotageCost"
            onOverride={handleOverride}
            sub
            baseName="PBT"
            currency={cur}
          />
          <EditableOverrideRow
            label="Tax"
            rate={outputs.taxRate}
            amount={outputs.taxAmount}
            fieldKey="taxAmount"
            onOverride={handleOverride}
            sub
            baseName="PBT"
            currency={cur}
          />
        </div>
      </section>

      <section data-testid="financial-drivers-funding">
        <SectionTitle>Funding &amp; capital</SectionTitle>
        <div className="space-y-1.5 md:space-y-px">
          <DriverField
            id="feasibilityStudy"
            label="Feasib."
            value={inputs.feasibilityStudy}
            onChange={setInputField('feasibilityStudy')}
            unit={cur}
            step={1000}
          />
          <EditableOverrideRow
            label="Equity"
            rate={outputs.equityRate}
            amount={outputs.equityRequired}
            fieldKey="equityRequired"
            onOverride={handleOverride}
            baseName={sourceLabels[outputs.equityBaseSource] || outputs.equityBaseSource}
            sourceSelector={(
              <SourceSelect
                value={inputs.capitalSources?.equity || 'landCost'}
                onChange={(s) => handleSourceChange('equity', s)}
                testId="financial-driver-source-equity"
              />
            )}
            currency={cur}
          />
          <EditableOverrideRow
            label="Bank"
            rate={outputs.bankDebtRate}
            amount={outputs.bankDebtAmount}
            fieldKey="bankDebt"
            onOverride={handleOverride}
            baseName={sourceLabels[outputs.bankDebtBaseSource] || outputs.bankDebtBaseSource}
            sourceSelector={(
              <SourceSelect
                value={inputs.capitalSources?.bankDebt || inputs.capitalSources?.debt || 'landCost'}
                onChange={(s) => handleSourceChange('bankDebt', s)}
                testId="financial-driver-source-bank"
              />
            )}
            currency={cur}
          />
          <DriverField
            id="bank-constructionFacility"
            label="Build loan"
            value={bank.constructionFacility ?? 0}
            onChange={(v) => setBankField('constructionFacility', Math.max(0, Number(v) || 0))}
            unit={cur}
            step={100000}
            min={0}
            testId="financial-driver-bank-constructionFacility"
          />
          <EditableOverrideRow
            label="Private"
            rate={outputs.privateCapitalRate}
            amount={outputs.privateCapitalAmount}
            fieldKey="privateCapital"
            onOverride={handleOverride}
            baseName={sourceLabels[outputs.privateCapitalBaseSource] || outputs.privateCapitalBaseSource}
            sourceSelector={(
              <SourceSelect
                value={inputs.capitalSources?.privateCapital || 'landCost'}
                onChange={(s) => handleSourceChange('privateCapital', s)}
                testId="financial-driver-source-private"
              />
            )}
            currency={cur}
          />
          <EditableOverrideRow
            label="Fees"
            rate={outputs.feesRate}
            amount={outputs.upfrontFees}
            fieldKey="upfrontFees"
            onOverride={handleOverride}
            baseName={sourceLabels[outputs.feesBaseSource] || outputs.feesBaseSource}
            sourceSelector={(
              <SourceSelect
                value={inputs.capitalSources?.fees || 'totalDevCost'}
                onChange={(s) => handleSourceChange('fees', s)}
                testId="financial-driver-source-fees"
              />
            )}
            currency={cur}
          />
          <AssumptionPctField
            label="Bank int."
            value={bank.interestRate}
            onChange={(v) => setBankField('interestRate', v)}
            testId="financial-driver-bank-interestRate"
          />
          <DriverField
            id="bank-durationMonths"
            label="Duration"
            value={bank.durationMonths ?? 18}
            onChange={(v) => setBankField('durationMonths', Math.max(0, Math.round(Number(v) || 0)))}
            unit="mo"
            step={1}
            min={0}
            max={120}
            testId="financial-driver-bank-durationMonths"
          />
          <AssumptionPctField
            label="Setup"
            value={bank.setupFees}
            onChange={(v) => setBankField('setupFees', v)}
            testId="financial-driver-bank-setupFees"
          />
        </div>
      </section>

      <section
        className="rounded border border-gray-100 bg-gray-50/80 px-1.5 py-1"
        data-testid="financial-drivers-derived"
      >
        <SectionTitle>Derived (read-only)</SectionTitle>
        <ReadOnlyMetric label="Revenue" value={outputs.totalRevenue} currency={cur} testId="financial-driver-derived-revenue" />
        <ReadOnlyMetric label="Dev cost" value={outputs.totalDevCost} currency={cur} testId="financial-driver-derived-devCost" />
        <ReadOnlyMetric label="Profit" value={outputs.finalNetProfit} currency={cur} testId="financial-driver-derived-profit" />
        <ReadOnlyMetric label="Capital" value={outputs.capitalInvested} currency={cur} testId="financial-driver-derived-capital" />
      </section>
    </div>
  );

  return (
    <aside
      className="w-full lg:w-[220px] shrink-0 bg-white border-b lg:border-b-0 lg:border-r border-gray-200 lg:overflow-y-auto"
      data-testid="financial-drivers-panel"
    >
      <div className="lg:hidden flex items-center justify-between px-3 py-2 border-b border-gray-100">
        <span className="text-[11px] font-semibold text-gray-700 uppercase tracking-wide">
          Financial inputs
        </span>
        <button
          type="button"
          className="text-[11px] text-blue-700 font-medium px-2 py-1 rounded hover:bg-blue-50"
          onClick={() => setMobileOpen((o) => !o)}
          data-testid="financial-drivers-mobile-toggle"
          aria-expanded={mobileOpen}
        >
          {mobileOpen ? 'Hide' : 'Show'}
        </button>
      </div>
      <div className={`p-3 md:p-1.5 ${mobileOpen ? 'block' : 'hidden'} lg:block`}>
        <div className="hidden lg:block text-[10px] font-semibold text-gray-700 uppercase tracking-wide mb-1.5">
          Financial inputs
        </div>
        {body}
      </div>
    </aside>
  );
}
